import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  POLISH_SPEECH_RATES,
  usePolishTextToSpeech,
  type PolishSpeechRate,
} from '@shared/speech/usePolishTextToSpeech';
import {
  createLazySpeechQueue,
  resolveFragmentPosition,
  shouldPromptNextChapter,
  type PolishSpeechFilters,
  type PolishSpeechQueue,
} from '@shared/speech/polishSpeechQueue';
import {
  clearPolishSpeechProgress,
  loadPolishSpeechProgress,
  savePolishSpeechProgress,
  type PolishSpeechProgress,
} from '@shared/speech/polishSpeechProgress';
import { useGamePrefs } from '../../state/GamePrefsProvider';

type Props = {
  chunks?: readonly string[];
  buildQueue?: (filters: PolishSpeechFilters) => PolishSpeechQueue;
  gameId?: string;
  sourceKey: string;
  onActiveFragmentChange?: (fragmentId: string | null) => void;
  onAdvanceChapter?: () => void;
  bottomOffset?: number;
};

type InsetCtx = {
  bottomInset: number;
  setBottomBarHeight: (height: number) => void;
};

const PolishSpeechBottomInsetContext = createContext<InsetCtx>({
  bottomInset: 0,
  setBottomBarHeight: () => undefined,
});

/** Extra gap between chapter nav / text end and the fixed player top. */
const READING_PLAYER_CONTENT_GAP = 32;

function readCssPx(varName: string, fallback = 0): number {
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!raw) return fallback;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function computeReadingPlayerInset(panelHeight: number): number {
  if (panelHeight <= 0) return 0;
  const gutter = readCssPx('--app-gutter', 8);
  const safeBottom = readCssPx('--safe-bottom', 0);
  const gap = readCssPx('--reading-player-gap', READING_PLAYER_CONTENT_GAP);
  // Panel sits at bottom: gutter + safe. Reserve that stack + panel + content gap.
  return Math.ceil(panelHeight + gutter + safeBottom + gap);
}

/** Publish measured player height so content/TOC padding clears the fixed bar. */
export function publishReadingPlayerInset(panelHeight: number) {
  const root = document.documentElement;
  if (panelHeight <= 0) {
    root.style.setProperty('--reading-player-height', '0px');
    root.style.setProperty('--reading-player-inset', '0px');
    root.style.setProperty('--speech-bar-reserve', '0px');
    root.removeAttribute('data-reading-player');
    return 0;
  }
  const inset = computeReadingPlayerInset(panelHeight);
  root.style.setProperty(
    '--reading-player-height',
    `${Math.ceil(panelHeight)}px`
  );
  root.style.setProperty('--reading-player-inset', `${inset}px`);
  // Legacy alias used by older helpers / scroll clearance.
  root.style.setProperty('--speech-bar-reserve', `${inset}px`);
  root.setAttribute('data-reading-player', 'open');
  return inset;
}

export function PolishSpeechBottomInsetProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const value = useMemo(
    () => ({
      bottomInset: computeReadingPlayerInset(bottomBarHeight),
      setBottomBarHeight,
    }),
    [bottomBarHeight]
  );
  return (
    <PolishSpeechBottomInsetContext.Provider value={value}>
      {children}
    </PolishSpeechBottomInsetContext.Provider>
  );
}

export function usePolishSpeechBottomInset() {
  return useContext(PolishSpeechBottomInsetContext).bottomInset;
}

export function usePolishSpeechContentBottomPadding(basePadding: number) {
  return basePadding + usePolishSpeechBottomInset();
}

const stateLabels = {
  idle: 'Czytnik jest gotowy',
  speaking: 'Czytnik odtwarza treść',
  paused: 'Czytnik jest wstrzymany',
  stopped: 'Czytnik został zatrzymany',
};

export function PolishSpeechReaderPanel({
  chunks = [],
  buildQueue,
  gameId,
  sourceKey,
  onActiveFragmentChange,
  onAdvanceChapter,
  bottomOffset = 0,
}: Props) {
  const reader = usePolishTextToSpeech();
  const { setBottomBarHeight } = useContext(PolishSpeechBottomInsetContext);
  const { getPrefs, patchPrefs } = useGamePrefs();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isWide, setIsWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 680 : true
  );
  const progressGameId = gameId ?? sourceKey.split(':')[0];
  const prefs = getPrefs(progressGameId);
  const previousSourceKey = useRef(sourceKey);
  const [resumeProgress, setResumeProgress] =
    useState<PolishSpeechProgress | null>(null);
  const [expanded, setExpanded] = useState(prefs.readingBarExpanded);
  const [hidden, setHidden] = useState(prefs.readingBarHidden);
  const [chapterPrompt, setChapterPrompt] = useState(false);

  const setExpandedPersist = (value: boolean | ((prev: boolean) => boolean)) => {
    setExpanded((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      patchPrefs(progressGameId, { readingBarExpanded: next });
      return next;
    });
  };

  const setHiddenPersist = (value: boolean) => {
    setHidden(value);
    patchPrefs(progressGameId, { readingBarHidden: value });
  };

  const fallbackQueue = useMemo(
    () =>
      createLazySpeechQueue(
        chunks.map((text, index) => ({ id: `chunk-${index}`, text }))
      ),
    [chunks]
  );
  const queue = buildQueue?.(reader.settings.filters) ?? fallbackQueue;
  const isSpeaking = reader.state === 'speaking';
  const isPaused = reader.state === 'paused';
  const activeFragment = queue.getBatch(reader.currentIndex, 1)[0]?.id ?? null;
  const currentFragment =
    queue.getBatch(reader.currentIndex, 1)[0]?.text ?? 'Brak fragmentu';
  const chapterTitle = queue.getBatch(0, 1)[0]?.text ?? 'Czytnik instrukcji';
  const fragmentNumber = queue.total === 0 ? 0 : reader.currentIndex + 1;
  const estimatedMinutes = Math.max(
    0,
    Math.ceil(
      ((queue.total - reader.currentIndex - 1) * 0.22) / reader.speed
    )
  );
  const progress =
    queue.total > 1 ? reader.currentIndex / (queue.total - 1) : 0;

  const syncPanelMetrics = useCallback(() => {
    const node = panelRef.current;
    if (!node || hidden) {
      setBottomBarHeight(0);
      publishReadingPlayerInset(0);
      return;
    }

    // Align fixed panel with the manual content column on wide layouts
    // so it does not cover the TOC or steal its clicks. Width can affect height.
    const content = document.querySelector(
      '.manual-screen .reader-panel'
    ) as HTMLElement | null;
    if (content && window.innerWidth >= 960) {
      const rect = content.getBoundingClientRect();
      node.style.left = `${Math.max(0, rect.left)}px`;
      node.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
      node.style.width = 'auto';
      node.style.maxWidth = 'none';
    } else {
      node.style.removeProperty('left');
      node.style.removeProperty('right');
      node.style.removeProperty('width');
      node.style.removeProperty('max-width');
    }

    const height = node.getBoundingClientRect().height;
    setBottomBarHeight(height);
    publishReadingPlayerInset(height);
  }, [hidden, setBottomBarHeight]);

  useEffect(() => {
    const onResize = () => {
      setIsWide(window.innerWidth >= 680);
      syncPanelMetrics();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncPanelMetrics]);

  useEffect(() => {
    if (previousSourceKey.current === sourceKey) return;
    previousSourceKey.current = sourceKey;
    setResumeProgress(null);
    setChapterPrompt(false);
    setHiddenPersist(false);
    void reader.stop();
  }, [reader.stop, sourceKey]);

  useEffect(() => {
    if (hidden) {
      setBottomBarHeight(0);
      publishReadingPlayerInset(0);
    }
  }, [hidden, setBottomBarHeight]);

  useEffect(
    () => () => {
      setBottomBarHeight(0);
      publishReadingPlayerInset(0);
    },
    [setBottomBarHeight]
  );

  useEffect(() => {
    const node = panelRef.current;
    if (!node || hidden) {
      publishReadingPlayerInset(0);
      return;
    }
    const ro = new ResizeObserver(() => {
      syncPanelMetrics();
    });
    ro.observe(node);
    // Immediate publish so content inset is correct before first paint settles.
    syncPanelMetrics();
    return () => {
      ro.disconnect();
    };
  }, [hidden, expanded, setBottomBarHeight, resumeProgress, chapterPrompt, syncPanelMetrics]);

  useEffect(() => {
    onActiveFragmentChange?.(isSpeaking || isPaused ? activeFragment : null);
  }, [activeFragment, isPaused, isSpeaking, onActiveFragmentChange]);

  useEffect(() => {
    if (!reader.settings.rememberProgress) return;
    void loadPolishSpeechProgress(progressGameId).then((saved) => {
      if (saved?.chapterId === sourceKey) setResumeProgress(saved);
    });
  }, [progressGameId, reader.settings.rememberProgress, sourceKey]);

  useEffect(() => {
    if (!reader.settings.rememberProgress || reader.total === 0) return;
    const fragment = queue.getBatch(reader.currentIndex, 1)[0];
    if (!fragment) return;
    void savePolishSpeechProgress(progressGameId, {
      chapterId: sourceKey,
      paragraphId: fragment.id,
      queuePosition: reader.currentIndex,
    });
  }, [
    progressGameId,
    queue,
    reader.currentIndex,
    reader.settings.rememberProgress,
    reader.total,
    sourceKey,
  ]);

  useEffect(() => {
    if (
      shouldPromptNextChapter(
        reader.state,
        reader.currentIndex,
        reader.total,
        reader.settings.autoAdvanceChapter,
        Boolean(onAdvanceChapter)
      )
    ) {
      setChapterPrompt(true);
    }
  }, [
    onAdvanceChapter,
    reader.currentIndex,
    reader.settings.autoAdvanceChapter,
    reader.state,
    reader.total,
  ]);

  useEffect(() => {
    if (!chapterPrompt || !onAdvanceChapter) return;
    const timeout = setTimeout(onAdvanceChapter, 5000);
    return () => clearTimeout(timeout);
  }, [chapterPrompt, onAdvanceChapter]);

  const start = useCallback(
    (position = 0) => {
      setHiddenPersist(false);
      void reader.start(queue, position);
    },
    [queue, reader]
  );

  const updateFilters = (filters: PolishSpeechFilters) =>
    reader.setSettings({ ...reader.settings, filters });

  const seek = (clientX: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || queue.total === 0) return;
    const ratio = maxRatio(clientX, rect);
    const nextIndex = Math.round(ratio * (queue.total - 1));
    void reader.jumpTo(nextIndex);
  };

  const togglePlayback = () => {
    if (isSpeaking) void reader.pause();
    else if (isPaused) void reader.resume();
    else start(reader.currentIndex);
  };

  if (hidden) {
    return (
      <button
        type="button"
        className="speech-show-btn"
        data-testid="speech-show"
        style={bottomOffset ? { bottom: bottomOffset } : undefined}
        onClick={() => setHiddenPersist(false)}
        aria-label="Pokaż czytnik instrukcji"
      >
        🔊 Czytnik
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`speech-player${isWide ? ' wide' : ''}${expanded ? ' expanded' : ''}`}
      data-testid="reading-player"
      data-speech-reader-panel=""
      aria-live="polite"
      aria-label={`Czytnik instrukcji. ${stateLabels[reader.state]}. ${fragmentNumber} z ${queue.total}.`}
    >
      <div className="speech-primary-row">
        <span className="speech-speaker" aria-hidden>
          🔊
        </span>
        <div className="speech-chapter">
          <div className="speech-chapter-title">{chapterTitle}</div>
          <div className="speech-fragment">
            Fragment {fragmentNumber} z {queue.total}
          </div>
        </div>
        {isWide ? (
          <TransportControls
            reader={reader}
            total={queue.total}
            isSpeaking={isSpeaking}
            isPaused={isPaused}
            onPlayPause={togglePlayback}
          />
        ) : null}
        <button
          type="button"
          className="speech-icon-btn"
          data-testid="speech-play-pause"
          aria-label={
            isSpeaking
              ? 'Wstrzymaj czytanie'
              : isPaused
                ? 'Wznów czytanie'
                : 'Czytaj rozdział'
          }
          disabled={reader.voiceStatus === 'checking' || queue.total === 0}
          onClick={togglePlayback}
        >
          {isSpeaking ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          className="speech-icon-btn"
          data-testid="speech-expand"
          aria-expanded={expanded}
          aria-label={
            expanded
              ? 'Zwiń ustawienia czytnika'
              : 'Rozwiń ustawienia czytnika'
          }
          onClick={() => setExpandedPersist((value) => !value)}
        >
          {expanded ? '⌃' : '⌄'}
        </button>
        <button
          type="button"
          className="speech-icon-btn"
          data-testid="speech-hide"
          aria-label="Ukryj czytnik instrukcji"
          onClick={() => setHiddenPersist(true)}
        >
          ×
        </button>
      </div>

      {isWide || expanded ? (
        <div className="speech-details">
          {!isWide ? (
            <TransportControls
              reader={reader}
              total={queue.total}
              isSpeaking={isSpeaking}
              isPaused={isPaused}
              onPlayPause={togglePlayback}
            />
          ) : null}
          <div className="speech-meta-row">
            <span>Około {estimatedMinutes} min pozostało</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
          <button
            type="button"
            className="speech-slider"
            data-testid="speech-seek"
            aria-label={`Postęp czytania, ${fragmentNumber} z ${queue.total}`}
            onClick={(event) =>
              seek(event.clientX, event.currentTarget)
            }
          >
            <span className="speech-slider-track">
              <span
                className="speech-slider-fill"
                style={{ width: `${progress * 100}%` }}
              />
            </span>
          </button>
          {expanded ? (
            <>
              <div className="speech-speed-row">
                <span className="speech-setting-label">Tempo</span>
                {POLISH_SPEECH_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    className={`speech-rate-btn${reader.speed === rate ? ' selected' : ''}`}
                    data-testid={`speech-rate-${rate}`}
                    aria-pressed={reader.speed === rate}
                    onClick={() => reader.setSpeed(rate as PolishSpeechRate)}
                  >
                    {rate.toFixed(rate === 1 ? 1 : 2)}×
                  </button>
                ))}
                <button
                  type="button"
                  className="speech-stop-btn"
                  data-testid="speech-stop"
                  onClick={() => void reader.stop()}
                >
                  ■ Stop
                </button>
              </div>
              <div className="speech-filters">
                <FilterToggle
                  label="Przykłady"
                  value={reader.settings.filters.examples}
                  onChange={(examples) =>
                    updateFilters({ ...reader.settings.filters, examples })
                  }
                />
                <FilterToggle
                  label="FAQ"
                  value={reader.settings.filters.faq}
                  onChange={(faq) =>
                    updateFilters({ ...reader.settings.filters, faq })
                  }
                />
                <FilterToggle
                  label="Ostrzeżenia"
                  value={reader.settings.filters.warnings}
                  onChange={(warnings) =>
                    updateFilters({ ...reader.settings.filters, warnings })
                  }
                />
                <FilterToggle
                  label="Przypisy"
                  value={reader.settings.filters.footnotes}
                  onChange={(footnotes) =>
                    updateFilters({ ...reader.settings.filters, footnotes })
                  }
                />
                <FilterToggle
                  label="Auto-rozdział"
                  value={reader.settings.autoAdvanceChapter}
                  onChange={(autoAdvanceChapter) =>
                    reader.setSettings({
                      ...reader.settings,
                      autoAdvanceChapter,
                    })
                  }
                />
                <FilterToggle
                  label="Pamiętaj pozycję"
                  value={reader.settings.rememberProgress}
                  onChange={(rememberProgress) =>
                    reader.setSettings({
                      ...reader.settings,
                      rememberProgress,
                    })
                  }
                />
              </div>
              <p className="speech-current-text" data-testid="speech-current-text">
                {currentFragment}
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {resumeProgress ? (
        <div className="speech-prompt">
          <span>Kontynuować słuchanie?</span>
          <button
            type="button"
            className="speech-small-btn"
            data-testid="speech-resume-yes"
            onClick={() => {
              start(
                resolveFragmentPosition(queue, resumeProgress.paragraphId)
              );
              setResumeProgress(null);
            }}
          >
            Tak
          </button>
          <button
            type="button"
            className="speech-small-btn"
            data-testid="speech-resume-no"
            onClick={() => {
              setResumeProgress(null);
              void clearPolishSpeechProgress(progressGameId);
            }}
          >
            Nie
          </button>
        </div>
      ) : null}

      {chapterPrompt ? (
        <div className="speech-prompt">
          <span>Przejść do następnego rozdziału za 5 sekund?</span>
          <button
            type="button"
            className="speech-small-btn"
            onClick={onAdvanceChapter}
          >
            Tak
          </button>
          <button
            type="button"
            className="speech-small-btn"
            onClick={() => setChapterPrompt(false)}
          >
            Anuluj
          </button>
        </div>
      ) : null}

      {reader.voiceStatus === 'missing' ? (
        <p className="speech-error">
          Nie znaleziono polskiego głosu na urządzeniu.
        </p>
      ) : null}
      {reader.error ? <p className="speech-error">{reader.error}</p> : null}
    </div>
  );
}

function maxRatio(clientX: number, rect: DOMRect) {
  return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
}

function TransportControls({
  reader,
  total,
  isSpeaking,
  isPaused,
  onPlayPause,
}: {
  reader: ReturnType<typeof usePolishTextToSpeech>;
  total: number;
  isSpeaking: boolean;
  isPaused: boolean;
  onPlayPause: () => void;
}) {
  return (
    <div className="speech-transport">
      <button
        type="button"
        className="speech-icon-btn"
        data-testid="speech-prev"
        disabled={total === 0 || reader.currentIndex === 0}
        aria-label="Poprzedni fragment"
        onClick={() => void reader.previous()}
      >
        ⏮
      </button>
      <button
        type="button"
        className="speech-icon-btn"
        disabled={total === 0}
        aria-label={
          isSpeaking
            ? 'Wstrzymaj czytanie'
            : isPaused
              ? 'Wznów czytanie'
              : 'Czytaj rozdział'
        }
        onClick={onPlayPause}
      >
        {isSpeaking ? '❚❚' : '▶'}
      </button>
      <button
        type="button"
        className="speech-icon-btn"
        data-testid="speech-next-frag"
        disabled={total === 0 || reader.currentIndex >= total - 1}
        aria-label="Następny fragment"
        onClick={() => void reader.next()}
      >
        ⏭
      </button>
    </div>
  );
}

function FilterToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="speech-filter"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
    >
      {value ? '✓ ' : '○ '}
      {label}
    </button>
  );
}
